import { api } from './http-client';
import { rawClient } from './raw-client';
import {
  type ImpersonateRequest,
  type LoginRequest,
  type SendLoginCodeRequest,
  sessionEndpoints,
  type SessionResponse,
} from './session.endpoints';

/**
 * 인증 요청(`@d3/contract` auth). 로그인 계열은 세션이 없을 때 부르므로 인증 · 토큰 갱신을
 * 거치지 않는 `rawClient` 로 보낸다(401 = 로그인 실패이지 세션 만료가 아니다).
 * 대리 로그인만 대리인의 액세스 토큰이 필요해 공용 client(`api`)로 보낸다.
 */

/** 로그인 1단계 — 아이디 · 비밀번호 · 이메일 확인 후 인증번호 메일 발송 */
export async function sendLoginCode(body: SendLoginCodeRequest): Promise<void> {
  await rawClient.request({
    method: sessionEndpoints.sendLoginCode.method,
    url: sessionEndpoints.sendLoginCode.path,
    data: body,
  });
}

/**
 * 로그인 2단계 — 인증번호까지 확인하고 세션을 만든다(리프레시 쿠키 발급).
 * 세션에는 아직 반영하지 않는다 — 들어갈 때는 `enterSession` 으로 주소를 먼저 옮긴다.
 */
export async function loginWithCode(body: LoginRequest): Promise<SessionResponse> {
  const { data } = await rawClient.request<SessionResponse>({
    method: sessionEndpoints.login.method,
    url: sessionEndpoints.login.path,
    data: body,
  });
  return data;
}

/** 비밀번호 재설정 메일 요청. 가입되지 않은 이메일은 404 NOT_FOUND_PASSWORD_RESET_EMAIL(DD-100), 비활성 계정은 발송 없이 204 */
export async function requestPasswordReset(email: string): Promise<void> {
  await rawClient.request({
    method: sessionEndpoints.requestPasswordReset.method,
    url: sessionEndpoints.requestPasswordReset.path,
    data: { email },
  });
}

/** 메일 링크의 토큰으로 새 비밀번호를 정한다. 성공하면 그 계정의 세션이 모두 끝난다 */
export async function resetPassword(token: string, newPassword: string): Promise<void> {
  await rawClient.request({
    method: sessionEndpoints.resetPassword.method,
    url: sessionEndpoints.resetPassword.path,
    data: { token, newPassword },
  });
}

/**
 * 대리 로그인 토큰을 받는다(세션에 반영하지 않는다 — 신원 전환은 `switch-identity` 가 순서대로 한다).
 * 리프레시 쿠키는 바뀌지 않는다: 대리 토큰이 끝나 갱신하면 대리인 신원으로 돌아온다(be.md B-4).
 */
export function requestImpersonation(body: ImpersonateRequest): Promise<SessionResponse> {
  return api.request(sessionEndpoints.impersonate, { body });
}
